import React, { useState, useEffect, useRef } from 'react';
import { cn } from '../../utils/cn';

/**
 * Props for the SideNavAnimation component
 */
interface SideNavAnimationProps {
    /**
     * Whether we're in a nested level with a parent item
     */
    hasParent: boolean;

    /**
     * Current items to display
     */
    items: unknown[];

    /**
     * Header content to show when not in a nested level
     */
    header?: React.ReactNode;

    /**
     * Back button content to show when in a nested level
     */
    backButton?: React.ReactNode;

    /**
     * Main navigation content
     */
    children: React.ReactNode;

    /**
     * Secondary navigation content (appears below main navigation)
     */
    secondaryNav?: React.ReactNode;

    /**
     * Footer content
     */
    footer?: React.ReactNode;
}

/**
 * Component that handles animations between navigation levels
 * Manages transitions between header/back button and different navigation sets
 */
export const SideNavAnimation: React.FC<SideNavAnimationProps> = ({
    hasParent,
    items,
    header,
    backButton,
    children,
    secondaryNav,
    footer,
}) => {
    // Track previous hasParent value with a ref to avoid dependency cycles
    const prevHasParentRef = useRef(hasParent);

    // Animation state
    const [isAnimating, setIsAnimating] = useState(false);
    const [currentContent, setCurrentContent] = useState(children);
    const [previousContent, setPreviousContent] = useState(children);
    const [previousHasParent, setPreviousHasParent] = useState(hasParent);

    // Update content and handle animations when props change
    useEffect(() => {
        // Check if parent status changed by comparing with ref
        if (hasParent !== prevHasParentRef.current) {
            // Save current state as previous before updating
            setPreviousContent(currentContent);
            setPreviousHasParent(prevHasParentRef.current);

            // Start animation
            setIsAnimating(true);

            // After animation out completes, update content
            setTimeout(() => {
                setCurrentContent(children);
                setIsAnimating(false);
            }, 150);

            // Update the ref for next comparison
            prevHasParentRef.current = hasParent;
        } else if (!isAnimating) {
            // No animation needed, just update content
            setCurrentContent(children);
        }
    }, [hasParent, items, children, isAnimating, currentContent]);

    // Animation styles for both current and previous states
    const getAnimationStyles = (isCurrentView: boolean) => {
        if (!isAnimating) {
            return 'translate-x-0 opacity-100';
        }

        return cn(
            'opacity-0',
            isCurrentView
                ? '-translate-x-full' // When going deeper, child comes in from left
                : 'translate-x-full', // When going back, parent comes in from left
        );
    };

    // Create the navigation page content with proper structure
    const buildNavigationContent = (
        isCurrentView: boolean,
        content: React.ReactNode,
        isParentView: boolean,
    ) => (
        <>
            {/* Header or Back button */}
            <div className="mb-11.5">{isParentView ? header : backButton}</div>

            {/* Primary navigation content - main section */}
            <div className="mb-12">{content}</div>

            {/* Secondary navigation - always available at the bottom */}
            {secondaryNav && <div className="mb-4">{secondaryNav}</div>}

            {/* Footer */}
            {footer && <div className="mb-6">{footer}</div>}
        </>
    );

    // Render both previous and current states during animation
    return (
        <div className="relative flex min-h-full flex-col">
            {/* Previous state (animating out) */}
            {isAnimating && (
                <div
                    className={cn(
                        'absolute inset-0 flex h-full w-full transform flex-col transition-all duration-300 ease-in-out',
                        getAnimationStyles(false),
                    )}
                >
                    {buildNavigationContent(
                        false,
                        previousContent,
                        !previousHasParent,
                    )}
                </div>
            )}

            {/* Current state (always visible or animating in) */}
            <div
                className={cn(
                    'flex h-full w-full transform flex-col transition-all duration-300 ease-in-out',
                    getAnimationStyles(true),
                )}
            >
                {buildNavigationContent(true, currentContent, !hasParent)}
            </div>
        </div>
    );
};
